import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject, Ref } from 'react';
import { useState, useMemo, useRef, useEffect } from 'react';
import type { LocationsProps, LocationsRef, Region } from './Locations.data';
import styles from './Locations.module.scss';
import LocationSearchBar from '../../molecules/location-search-bar/LocationSearchBar';
import LocationDropdownItem from '../location-dropdown/LocationDropdown';
import { useGlobalStore } from '../../../stores/global';
import LocationItem from '../location-dropdown/LocationItem';
import ButtonGrid from '../button-grid/ButtonGrid';
import { filterLocations } from '../../../util/locations.utils';
import type { LocationBlokData } from '../location/Location.data';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';

export default function Locations({
  className,
  blok,
  ref,
}: LocationsProps & { ref?: Ref<LocationsRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const [searchValue, setSearchValue] = useState('');
  const [headerIconColor, setHeaderIconColor] = useState('');

  const { locationsList } = useGlobalStore();

  const regions: Array<Region> = useMemo(() => {
    if (!locationsList?.length) return [];
    const regions: Array<Region> = [];
    locationsList.forEach((location) => {
      const region = location.region[0];
      if (!regions.includes(region)) {
        regions.push(region);
      }
    });

    return regions.sort();
  }, [locationsList]);

  useThemeScrollBlock(elementRef, blok.use_accent_color);

  useEffect(() => {
    const updateHeaderIconColor = (): void => {
      const headerRotatingShape = document.getElementById('headerRotatingShape');
      const iconColor = headerRotatingShape
        ? window.getComputedStyle(headerRotatingShape).getPropertyValue('--icon-color').trim()
        : '';

      setHeaderIconColor(iconColor);
    };

    updateHeaderIconColor();

    const frameId = window.requestAnimationFrame(updateHeaderIconColor);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, []);

  return (
    <div ref={elementRef} className={classNames(styles.offices, className)} {...sbEditable(blok)}>
      <LocationSearchBar
        search_placeholder={blok.search_placeholder}
        title={blok.title}
        quick_links={blok.quick_links}
        quick_links_label={blok.quick_links_label}
        parentSetSearchValue={setSearchValue}
      />

      <ButtonGrid
        blok={{ text_items: blok.global_regions, _uid: '', component: 'buttonGrid' }}
        className={styles.buttonGrid}
      />

      {searchValue && locationsList ? (
        <ul>
          {(
            locationsList
              .filter((location: LocationBlokData) => filterLocations(location, searchValue))
              .sort((a, b) => a.city.localeCompare(b.city)) || []
          ).map((location, i) => (
            <LocationItem
              key={location._uid + searchValue}
              className={classNames(styles.cityContainer)}
              location={location}
              isOpen={!!searchValue}
              index={i}
              iconColor={headerIconColor}
            />
          ))}
        </ul>
      ) : (
        regions.map((region) => (
          <LocationDropdownItem
            key={region}
            region={region}
            iconColor={headerIconColor}
            link={
              blok.regions.find((r) => r.label.toLowerCase() === region.toLowerCase())?.button?.[0]
                .link
            }
            region_link_label={blok.region_link_label}
          />
        ))
      )}
    </div>
  );
}
